import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, Users, BookOpen, FileText, Sparkles, Baby, TrendingUp, AlertCircle } from 'lucide-react';
import { infantilAPI } from '../services/api';

const fadeUp = { hidden: { opacity: 0, y: 16 }, visible: { opacity: 1, y: 0, transition: { duration: 0.4 } } };
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.06 } } };

const KPICard = ({ label, value, icon: Icon, color, onClick }) => (
  <motion.div variants={fadeUp} onClick={onClick}
    className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm hover:shadow-md transition-all cursor-pointer group">
    <div className="flex items-center justify-between mb-3">
      <div className="p-2 rounded-xl" style={{ background: `${color}12` }}>
        <Icon className="h-5 w-5" style={{ color }} />
      </div>
    </div>
    <p className="text-3xl font-bold text-slate-800">{value}</p>
    <p className="text-xs text-slate-500 mt-1">{label}</p>
  </motion.div>
);

export default function InfantilDashboardPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    infantilAPI.dashboard().then(d => { setData(d); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="space-y-4 animate-pulse">
      <div className="h-20 rounded-2xl bg-slate-100" />
      <div className="grid grid-cols-3 gap-4">{[...Array(6)].map((_, i) => <div key={i} className="h-28 rounded-2xl bg-slate-100" />)}</div>
    </div>
  );

  const kpis = data?.kpis || {};
  const bncc = data?.bncc_coverage || [];
  const classStats = data?.class_stats || [];
  const recent = data?.recent_observations || [];

  return (
    <motion.div className="space-y-6" initial="hidden" animate="visible" variants={stagger} data-testid="infantil-dashboard">
      {/* Hero */}
      <motion.div variants={fadeUp} className="rounded-2xl p-6 relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #2E1A47 0%, #64327D 100%)' }}>
        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-1">
            <Baby className="h-5 w-5 text-[#2BB673]" />
            <span className="text-xs font-bold uppercase tracking-widest text-white/50">DNA Infantil 360 IA</span>
          </div>
          <h1 className="text-2xl font-bold text-white mb-1">Painel Pedagógico</h1>
          <p className="text-sm text-white/60">Educação Infantil — 1º Trimestre 2026</p>
        </div>
        <div className="absolute top-0 right-0 w-64 h-64 rounded-full opacity-10"
          style={{ background: 'radial-gradient(circle, #2BB673, transparent 70%)' }} />
      </motion.div>

      {/* KPIs */}
      <motion.div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" variants={stagger}>
        <KPICard label="Observações" value={kpis.total_observations || 0} icon={Eye} color="#64327D" onClick={() => navigate('/v2/infantil/observacoes')} />
        <KPICard label="Crianças Observadas" value={kpis.students_observed || 0} icon={Baby} color="#2BB673" onClick={() => navigate('/v2/infantil/crianca')} />
        <KPICard label="Turmas" value={kpis.total_classes || 0} icon={Users} color="#3B82F6" onClick={() => navigate('/v2/infantil/turma')} />
        <KPICard label="Portfólios" value={kpis.portfolios_created || 0} icon={BookOpen} color="#F59E0B" onClick={() => navigate('/v2/infantil/portfolios')} />
        <KPICard label="Relatórios" value={kpis.reports_generated || 0} icon={FileText} color="#EC4899" onClick={() => navigate('/v2/infantil/relatorios')} />
        <KPICard label="Campos BNCC" value={`${kpis.bncc_fields_covered || 0}/5`} icon={TrendingUp} color="#10B981" />
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* BNCC Coverage */}
        <motion.div variants={fadeUp} className="lg:col-span-2 bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
          <h3 className="text-sm font-bold text-slate-800 mb-4 flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-[#64327D]" /> Cobertura BNCC por Campo
          </h3>
          <div className="space-y-3">
            {bncc.map((f, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-32 text-xs text-slate-600 truncate">{f.field}</div>
                <div className="flex-1 h-8 rounded-lg bg-slate-50 overflow-hidden relative">
                  <motion.div
                    className="h-full rounded-lg"
                    style={{ backgroundColor: f.color, width: `${Math.min(100, (f.count / Math.max(1, kpis.total_observations)) * 100 * 5)}%` }}
                    initial={{ width: 0 }}
                    animate={{ width: `${Math.min(100, (f.count / Math.max(1, kpis.total_observations)) * 100 * 5)}%` }}
                    transition={{ duration: 0.8, delay: i * 0.1 }}
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-500">{f.count}</span>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Quick Actions */}
        <motion.div variants={fadeUp} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
          <h3 className="text-sm font-bold text-slate-800 mb-4">Ações Rápidas</h3>
          <div className="space-y-2">
            {[
              { label: 'Novo Registro', icon: Sparkles, color: '#2BB673', path: '/v2/infantil/novo-registro' },
              { label: 'Ver Observações', icon: Eye, color: '#64327D', path: '/v2/infantil/observacoes' },
              { label: 'Abrir Turma', icon: Users, color: '#3B82F6', path: '/v2/infantil/turma' },
              { label: 'Criar Portfólio', icon: BookOpen, color: '#F59E0B', path: '/v2/infantil/portfolios' },
            ].map((a, i) => (
              <button key={i} onClick={() => navigate(a.path)}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-all text-left group">
                <div className="p-2 rounded-lg" style={{ background: `${a.color}12` }}>
                  <a.icon className="h-4 w-4" style={{ color: a.color }} />
                </div>
                <span className="text-sm font-medium text-slate-700 group-hover:text-slate-900">{a.label}</span>
              </button>
            ))}
          </div>

          {/* Class Stats Mini */}
          {classStats.length > 0 && (
            <div className="mt-5 pt-4 border-t border-slate-100">
              <p className="text-xs font-bold text-slate-500 mb-2 uppercase tracking-wider">Turmas</p>
              {classStats.slice(0, 5).map((c, i) => (
                <div key={i} className="flex items-center justify-between py-1.5 text-xs">
                  <span className="text-slate-600 truncate">{c.name}</span>
                  <span className="font-bold text-[#64327D]">{c.observations} obs</span>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      </div>

      {/* Recent Observations Timeline */}
      {recent.length > 0 && (
        <motion.div variants={fadeUp} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
          <h3 className="text-sm font-bold text-slate-800 mb-4 flex items-center gap-2">
            <Eye className="h-4 w-4 text-[#64327D]" /> Observações Recentes
          </h3>
          <div className="space-y-3">
            {recent.slice(0, 6).map((obs, i) => (
              <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-all cursor-pointer"
                onClick={() => navigate(`/v2/infantil/observacoes`)}>
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: '#5B3B8C15' }}>
                  <Eye className="h-4 w-4 text-[#64327D]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-700 truncate">{obs.title}</p>
                  <p className="text-xs text-slate-500 truncate">{obs.observation_text?.substring(0, 80)}...</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#DDF7EC] text-[#2BB673] font-medium">{obs.context}</span>
                    <span className="text-[10px] text-slate-400">{obs.date}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </motion.div>
  );
}
